import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { Node, Edge } from '@swimlane/ngx-graph';
import { IDependentConfiguration } from '@app/interfaces/IDependentChecks';
import { Messages } from '@app/util/messages.util';

/** Graph color and opacity constants */
const GRAPH_COLORS = {
  BLOCKING: '#FF2A2B',
  NON_BLOCKING: '#1976d2',
  EDGE: '#666',
} as const;

const GRAPH_OPACITY = {
  ACTIVE: 1,
  FADED: 0.4,
} as const;

/**
 * DEPENDENCY GRAPH COMPONENT
 * 
 * Reusable graph visualization component using ngx-graph library.
 * Displays workflow dependencies as a directed acyclic graph (DAG).
 * 
 * Features:
 * - Interactive node selection and deletion
 * - Edit target connections via popup dialog
 * - Cycle detection when modifying connections
 * 
 * @component
 */
@Component({
  selector: 'app-dependency-graph',
  templateUrl: './dependency-graph.component.html',
  styleUrls: ['./dependency-graph.component.scss']
})
export class DependencyGraphComponent implements OnChanges {
  // Cache for O(1) config lookup by ID (rebuilt when graphConfigurations changes)
  private configMap: Map<string, IDependentConfiguration> = new Map();

  // Graph data inputs - nodes and connections
  @Input() nodes: Node[] = [];
  @Input() links: Edge[] = [];
  
  // Graph display configuration
  @Input() autoZoom: boolean = false;  // Disabled - let graph use natural layout
  @Input() autoCenter: boolean = false;  // Disabled - let graph use natural layout
  @Input() draggingEnabled: boolean = false;
  @Input() panningEnabled: boolean = true;
  @Input() showMiniMap: boolean = false;
  
  // Configuration data for editing connections
  @Input() graphConfigurations: IDependentConfiguration[] = [];
  @Input() isFormEditable: boolean = true;

  // Events emitted to parent component
  @Output() nodeDelete = new EventEmitter<string>();
  @Output() targetsUpdated = new EventEmitter<void>();
  @Output() graphCleared = new EventEmitter<void>();

  // ngx-graph configuration object
  graphConfig = {
    // Zoom settings
    enableZoom: true,
    panOnZoom: true,
    zoomSpeed: 0.1,
    minZoomLevel: 0.1,
    maxZoomLevel: 4,
    
    // Layout algorithm
    layout: 'dagre',
    
    // Layout settings for Dagre algorithm
    layoutSettings: {
      orientation: 'LR',      // Left to Right layout
      marginX: 20,           // Horizontal margin
      marginY: 20,           // Vertical margin
      edgePadding: 50,       // Space for edge routing
      rankPadding: 100,      // Spacing between ranks (columns in LR)
      nodePadding: 40        // Spacing between nodes in same rank
    }
  };

  // Popup state for editing target connections
  showEditTargetsPopup = false;
  selectedSourceNodeForEdit: IDependentConfiguration | null = null;
  availableTargetConfigs: IDependentConfiguration[] = [];
  selectedTargetConfigIds: string[] = [];
  private originalTargetConfigIds: string[] = []; // Track original connections to detect changes
  editPopupError: string = '';
  isBlocking: boolean = true;
  isBlockingTooltip = Messages.dependentChecks.graph.isBlockingTooltip;
  blockingLegendTooltip = Messages.dependentChecks.graph.blockingLegendTooltip;
  nonBlockingLegendTooltip = Messages.dependentChecks.graph.nonBlockingLegendTooltip;
  deletedLegendTooltip = Messages.dependentChecks.graph.deletedTooltip;
  inactiveLegendTooltip = Messages.dependentChecks.graph.inactiveTooltip;
  nonBlockingDisabledTooltip = Messages.dependentChecks.graph.nonBlockingDisabledTooltip;
  private popupJustOpened = false; // Guard against immediate close from double-click

  /**
   * Handle node selection - opens edit targets popup
   * Uses setTimeout to break event chain and prevent double-click issues
   * @param node - The selected graph node
   */
  onNodeSelect(node: Node): void {
    if (!this.isFormEditable) return;
    if (this.showEditTargetsPopup) return; // Prevent re-opening if already open
    
    const config = this.graphConfigurations.find(c => c.id === node.id);
    if (config) {
      // Deleted or inactive nodes are not editable - can only be removed via delete button
      if (config.graphData?.deleted === true || config.graphData?.active === false) return;
      // Use setTimeout to defer popup opening and break event propagation chain
      setTimeout(() => this.openEditTargetsPopup(config), 0);
    }
  }

  /**
   * Handle node deletion - prevents event bubbling to avoid selecting node
   * @param nodeId - ID of the node to delete
   * @param event - Click event to stop propagation
   */
  onDeleteNode(nodeId: string, event: Event): void {
    event.stopPropagation(); // Prevent node selection when clicking delete
    this.nodeDelete.emit(nodeId);
  }

  /**
   * Handle Clear All button click
   * Emits event to parent component to clear all configurations from graph
   */
  onClearAll(): void {
    this.graphCleared.emit();
  }

  /**
   * Open edit targets popup for a node
   * Shows dialog to modify which nodes this configuration connects to
   * @param sourceConfig - The configuration whose connections are being edited
   */
  openEditTargetsPopup(sourceConfig: IDependentConfiguration): void {
    this.selectedSourceNodeForEdit = sourceConfig;
    // Get all other nodes as potential targets (exclude the selected node itself)
    this.availableTargetConfigs = this.graphConfigurations.filter(c => c.id !== sourceConfig.id);
    // Get currently connected targets for this source
    this.selectedTargetConfigIds = (sourceConfig.graphData?.connections || []).map((conn: any) => conn.target);
    // Store original connections to detect if they changed
    this.originalTargetConfigIds = [...this.selectedTargetConfigIds];
    // Get current isBlocking value
    this.isBlocking = sourceConfig.graphData?.isBlocking ?? true;
    this.editPopupError = '';
    this.showEditTargetsPopup = true;
    
    // Set guard flag to prevent immediate close from double-click events
    this.popupJustOpened = true;
    setTimeout(() => this.popupJustOpened = false, 300);
  }

  /**
   * Save updated targets for the selected node.
   * Validates for cycles before updating connections.
   * Emits targetsUpdated event to refresh parent component's graph.
   */
  saveUpdatedTargets(): void {
    if (!this.selectedSourceNodeForEdit) return;

    const sourceConfig = this.selectedSourceNodeForEdit;
    this.editPopupError = '';
    
    // Check if connections actually changed
    const connectionsChanged = this.haveConnectionsChanged();
    
    // If only isBlocking changed (no connection changes), skip validation
    if (!connectionsChanged) {
      this.updateConnectionsAndClose(sourceConfig);
      return;
    }
    
    if (this.selectedTargetConfigIds.length === 0) {
      this.updateConnectionsAndClose(sourceConfig);
      return;
    }

    // Validate for cycles only if connections changed
    const cycleError = this.checkForCycles(sourceConfig);
    if (cycleError) {
      this.editPopupError = cycleError;
      return;
    }

    // Validation passed - update and close
    this.updateConnectionsAndClose(sourceConfig);
  }

  /**
   * Check if connections have changed from original
   */
  private haveConnectionsChanged(): boolean {
    if (this.selectedTargetConfigIds.length !== this.originalTargetConfigIds.length) {
      return true;
    }
    const currentSet = new Set(this.selectedTargetConfigIds);
    const originalSet = new Set(this.originalTargetConfigIds);
    return ![...currentSet].every(id => originalSet.has(id));
  }

  /**
   * Close the edit targets popup and reset all state
   */
  closeEditTargetsPopup(): void {
    this.showEditTargetsPopup = false;
    this.selectedSourceNodeForEdit = null;
    this.availableTargetConfigs = [];
    this.selectedTargetConfigIds = [];
    this.originalTargetConfigIds = [];
    this.editPopupError = '';
    this.isBlocking = true;
    this.popupJustOpened = false;
  }

  /**
   * Close popup when clicking overlay (guards against double-click close)
   */
  closePopupFromOverlay(): void {
    // Ignore overlay click if popup just opened (prevents double-click from closing)
    if (this.popupJustOpened) return;
    this.closeEditTargetsPopup();
  }

  /**
   * Check for cycles in the graph with new connections
   * @param sourceConfig - The source configuration
   * @returns Error message if cycle detected, null otherwise
   */
  private checkForCycles(sourceConfig: IDependentConfiguration): string | null {
    const cyclicTargets = this.selectedTargetConfigIds.filter(targetId => 
      this.wouldCreateCycle(sourceConfig.id, [targetId])
    );
    
    if (cyclicTargets.length > 0) {
      const targetNames = cyclicTargets
        .map(id => this.graphConfigurations.find(c => c.id === id)?.name || id)
        .join(', ');
      const connection = `"${sourceConfig.name}" → [${targetNames}]`;
      return Messages.dependentChecks.graph.cycleCreate
        .replace('{connection}', connection);
    }
    
    return null;
  }

  /**
   * Update connections and close the popup
   * @param sourceConfig - The source configuration to update
   */
  private updateConnectionsAndClose(sourceConfig: IDependentConfiguration): void {
    sourceConfig.graphData ??= { connections: [] };
    
    // Preserve existing active/deleted flags from graphData
    const { active, deleted } = sourceConfig.graphData;
    
    sourceConfig.graphData.connections = this.selectedTargetConfigIds.map(targetId => ({
      source: sourceConfig.id,
      target: targetId,
      label: ''
    }));
    // Standalone/leaf nodes (no outgoing connections) are always non-blocking
    sourceConfig.graphData.isBlocking = this.selectedTargetConfigIds.length > 0 ? this.isBlocking : false;
    sourceConfig.graphData.active = active;
    sourceConfig.graphData.deleted = deleted;
    
    this.targetsUpdated.emit();
    this.closeEditTargetsPopup();
  }

  /**
   * Public method to check for cycles - used by parent components
   */
  checkWouldCreateCycle(sourceId: string, targetIds: string[]): boolean {
    return this.wouldCreateCycle(sourceId, targetIds);
  }

  /**
   * Check if adding connections would create a cycle in the graph
   * Uses adjacency list representation and DFS to detect cycles
   * 
   * @param sourceId - The source node ID
   * @param targetIds - Array of target node IDs to connect to
   * @returns true if any connection would create a cycle
   */
  private wouldCreateCycle(sourceId: string, targetIds: string[]): boolean {
    // Build adjacency list from current graph state
    const adjacencyList = new Map<string, string[]>();
    
    // Flatten all connections and populate adjacency list
    this.graphConfigurations
      .filter(config => config.graphData?.connections)
      .flatMap(config => config.graphData!.connections)
      .forEach((conn: any) => {
        if (!adjacencyList.has(conn.source)) adjacencyList.set(conn.source, []);
        adjacencyList.get(conn.source)!.push(conn.target);
      });
    
    // Add new connections temporarily to test for cycles
    const currentTargets = adjacencyList.get(sourceId) || [];
    adjacencyList.set(sourceId, [...currentTargets, ...targetIds]);
    
    // Check each target for cycles using depth-first search
    return targetIds.some(targetId => this.hasCycle(targetId, sourceId, adjacencyList, new Set()));
  }

  /**
   * Recursive depth-first search to detect cycles
   * Checks if we can reach the target node from current node
   * 
   * @param current - Current node being visited
   * @param target - Target node we're looking for (creates cycle if found)
   * @param adjacencyList - Graph representation as adjacency list
   * @param visited - Set of already visited nodes to avoid infinite loops
   * @returns true if cycle detected (current path leads back to target)
   */
  private hasCycle(current: string, target: string, adjacencyList: Map<string, string[]>, visited: Set<string>): boolean {
    if (current === target) return true;  // Cycle found!
    if (visited.has(current)) return false;  // Already checked this path
    
    visited.add(current);
    const neighbors = adjacencyList.get(current) || [];
    return neighbors.some(neighbor => this.hasCycle(neighbor, target, adjacencyList, visited));
  }

  /**
   * Calculate dynamic node width based on label length
   * Node width expands to accommodate full text without truncation
   * 
   * @param label - The node's display text
   * @returns Calculated width in pixels (minimum 70px, no maximum)
   */
  getNodeWidth(label: string): number {
    const minWidth = 70;
    const charWidth = 6.5;   // Approximate width per character for 12px font
    const leftPadding = 10;   // Space before text
    const rightPadding = 20;  // Space after text (includes delete button space)
    const calculatedWidth = (label?.length || 0) * charWidth + leftPadding + rightPadding;
    return Math.max(calculatedWidth, minWidth);
  }

  // ============================================================================
  // BLOCKING NODE COLOR LOGIC
  // ============================================================================

  /**
   * Lifecycle hook - rebuild config map cache when graphConfigurations changes
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['graphConfigurations']) {
      this.rebuildConfigMap();
    }
  }

  /**
   * Rebuild the config map for O(1) lookups
   */
  private rebuildConfigMap(): void {
    this.configMap = new Map(this.graphConfigurations.map(c => [c.id, c]));
  }

  /**
   * Check if a node has outgoing connections
   * 
   * @param nodeId - The node ID to check
   * @returns true if the node has at least one outgoing connection
   */
  private hasOutgoingConnections(nodeId: string): boolean {
    const config = this.configMap.get(nodeId);
    return (config?.graphData?.connections || []).length > 0;
  }

  /**
   * Check if a node is blocking (centralized logic)
   * Standalone and leaf nodes (no outgoing connections) are always non-blocking
   * Uses cached config map for O(1) lookup
   * 
   * @param nodeId - The node ID to check
   * @returns true if blocking, false if non-blocking
   */
  private isNodeBlocking(nodeId: string): boolean {
    if (!this.hasOutgoingConnections(nodeId)) return false;
    const config = this.configMap.get(nodeId);
    return config?.graphData?.isBlocking ?? true;
  }

  /**
   * Check if a node is deleted
   * 
   * @param nodeId - The node ID to check
   * @returns true if node is deleted
   */
  private isNodeDeleted(nodeId: string): boolean {
    const config = this.configMap.get(nodeId);
    return config?.graphData?.deleted === true;
  }

  /**
   * Check if a node is inactive (active = false)
   * 
   * @param nodeId - The node ID to check
   * @returns true if node is inactive
   */
  private isNodeInactive(nodeId: string): boolean {
    const config = this.configMap.get(nodeId);
    return config?.graphData?.active === false;
  }

  /**
   * Get node stroke color based on blocking/deleted status
   * Grey for deleted nodes, red for blocking, blue for non-blocking
   * 
   * @param nodeId - The node ID to check
   * @returns Color string for the node stroke
   */
  getNodeStrokeColor(nodeId: string): string {
    if (this.isNodeDeleted(nodeId)) return GRAPH_COLORS.EDGE;
    return this.isNodeBlocking(nodeId) ? GRAPH_COLORS.BLOCKING : GRAPH_COLORS.NON_BLOCKING;
  }

  /**
   * Get node opacity based on active/deleted status
   * Deleted or inactive nodes are faded out (lower opacity) while keeping border color
   * 
   * @param nodeId - The node ID to check
   * @returns Opacity value (0-1)
   */
  getNodeOpacity(nodeId: string): number {
    if (this.isNodeDeleted(nodeId) || this.isNodeInactive(nodeId)) return GRAPH_OPACITY.FADED;
    return GRAPH_OPACITY.ACTIVE;
  }

  /**
   * Get tooltip text for a node based on its status
   * 
   * @param nodeId - The node ID to check
   * @returns Tooltip text describing the node status
   */
  getNodeTooltip(nodeId: string): string {
    if (this.isNodeDeleted(nodeId)) return Messages.dependentChecks.graph.deletedTooltip;
    if (this.isNodeInactive(nodeId)) return Messages.dependentChecks.graph.inactiveTooltip;
    return this.isNodeBlocking(nodeId) ? this.blockingLegendTooltip : this.nonBlockingLegendTooltip;
  }

  /**
   * Get edge stroke color
   * All edges use the same base color, opacity controls fading
   * 
   * @param link - The edge/link object
   * @returns Color string for the edge stroke
   */
  getEdgeStrokeColor(link: any): string {
    return GRAPH_COLORS.EDGE;
  }

  /**
   * Get edge opacity based on active/deleted status
   * Deleted or inactive edges are faded out
   * 
   * @param link - The edge/link object
   * @returns Opacity value (0-1)
   */
  getEdgeOpacity(link: any): number {
    if (link.data?.deleted === true || link.data?.active === false) return GRAPH_OPACITY.FADED;
    return GRAPH_OPACITY.ACTIVE;
  }

}